import React, { useState, useEffect } from "react";
import "./DynamicUpdatesPane.css";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

// Hook to check if an image URL is valid
function useImageExists(url) {
  const [valid, setValid] = useState(null);

  useEffect(() => {
    if (!url) {
      setValid(false);
      return;
    }
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => !cancelled && setValid(true);
    img.onerror = () => !cancelled && setValid(false);
    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [url]);

  return valid;
}

// StudentCard component for birthday/wavering students
function StudentCard({ student, onClick, children }) {
  const valid = useImageExists(student.imageURI);
  const src =
    valid === false || !student.imageURI || student.imageURI === ""
      ? "/default-avatar.png"
      : student.imageURI;
  return (
    <div className="birthday-card" onClick={onClick}>
      <div className="birthday-card-image">
        <img
          src={src}
          alt={`${student.firstName || ""} ${student.lastName || ""}`}
        />
      </div>
      <div className="birthday-card-content">{children}</div>
    </div>
  );
}

// Helper function to truncate text with ellipsis
const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const DynamicUpdatesPane = ({
  students = [],
  openBirthdayModal,
  waveringStudents = [],
  openWaveringModal,
  attendanceMap = {},
  events = [],
  setCurrentTab,
  professionalContent = null,
  openTrainingPlanModal,
}) => {
  const [currentPane, setCurrentPane] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const totalPanes = 4; // Updated to include the new training plan pane

  // Effect to handle the carousel rotation
  useEffect(() => {
    let interval;
    if (!isHovering) {
      interval = setInterval(() => {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentPane((prevPane) => (prevPane + 1) % totalPanes);
          setIsTransitioning(false);
        }, 300); // Match this with CSS transition duration
      }, 10000); // Switch every 10 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isHovering, waveringStudents]);

  // Navigation handlers
  const goToNextPane = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentPane((prevPane) => (prevPane + 1) % totalPanes);
      setIsTransitioning(false);
    }, 300);
  };

  const goToPreviousPane = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentPane((prevPane) => (prevPane - 1 + totalPanes) % totalPanes);
      setIsTransitioning(false);
    }, 300);
  };

  // Training plan related functions
  const getCurrentMonthAndWeek = () => {
    const now = new Date();
    const currentYear = now.getFullYear().toString();
    const currentMonth = (now.getMonth() + 1).toString(); // Remove padding, use "1" not "01"
    const currentWeek = Math.ceil(now.getDate() / 7);

    const yearData = professionalContent?.years?.[0]?.[currentYear];
    const monthData = yearData?.[currentMonth];
    const weekData = monthData?.weeks?.[currentWeek];

    return {
      year: currentYear,
      month: currentMonth,
      week: currentWeek,
      monthData,
      weekData
    };
  };

  const getMonthName = (monthNum) => {
    const months = [
      "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
      "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
    ];
    return months[parseInt(monthNum) - 1] || monthNum;
  };

  // Birthday related functions
  const getUpcomingBirthdays = () => {
    const today = new Date();
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(today.getDate() + 14);

    return students
      .filter((student) => {
        if (!student.birthday) return false;
        const birthday = student.birthday.toDate();
        const birthdayThisYear = new Date(
          today.getFullYear(),
          birthday.getMonth(),
          birthday.getDate()
        );
        return birthdayThisYear >= today && birthdayThisYear <= twoWeeksFromNow;
      })
      .sort((a, b) => {
        const aBirthday = a.birthday.toDate();
        const bBirthday = b.birthday.toDate();
        const aBirthdayThisYear = new Date(
          today.getFullYear(),
          aBirthday.getMonth(),
          aBirthday.getDate()
        );
        const bBirthdayThisYear = new Date(
          today.getFullYear(),
          bBirthday.getMonth(),
          bBirthday.getDate()
        );
        return aBirthdayThisYear - bBirthdayThisYear;
      });
  };

  const getClosestBirthdays = () => {
    return getUpcomingBirthdays().slice(0, 2);
  };

  const getDaysUntilBirthday = (birthday) => {
    const today = new Date();
    const birthdayDate = birthday.toDate();
    const birthdayThisYear = new Date(
      today.getFullYear(),
      birthdayDate.getMonth(),
      birthdayDate.getDate()
    );
    const diffTime = birthdayThisYear - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getNextAge = (birthday) => {
    const today = new Date();
    const birthdayDate = birthday.toDate();
    return today.getFullYear() - birthdayDate.getFullYear();
  };

  // Sub-pane components
  const SubPane1 = () => {
    const closestBirthdays = getClosestBirthdays();

    return (
      <div className="sub-pane">
        <h6>ימי הולדת</h6>
        <div className="birthday-cards">
          {closestBirthdays.length > 0 ? (
            closestBirthdays.map((student, index) => (
              <StudentCard
                key={index}
                student={student}
                onClick={openBirthdayModal}
              >
                <h4 className="student-name">{student.name}</h4>
                <div className="birthday-details">
                  <span className="next-age">
                    {student.gender === "זכר" ? "יהיה בן" : "תהיה בת"}{" "}
                    {getNextAge(student.birthday)} בעוד{" "}
                    {getDaysUntilBirthday(student.birthday)} ימים
                  </span>
                </div>
              </StudentCard>
            ))
          ) : (
            <p className="no-birthdays">אין ספורטאים</p>
          )}
        </div>
      </div>
    );
  };

  const SubPane2 = () => {
    const waveringPreview = waveringStudents.slice(0, 2);
    return (
      <div className="sub-pane">
        <h6>ספורטאים מתנדנדים</h6>
        <div className="birthday-cards">
          {waveringPreview.length > 0 ? (
            waveringPreview.map((student, index) => {
              const att = attendanceMap[student.id] || { present: 0, total: 0 };
              const missed = att.total - att.present;
              const missedPercent =
                att.total > 0 ? Math.round((missed / att.total) * 100) : 0;
              const genderText = student.gender === "נקבה" ? "החסירה" : "החסיר";
              return (
                <StudentCard
                  key={index}
                  student={student}
                  onClick={openWaveringModal}
                >
                  <h4 className="student-name">{student.name}</h4>
                  <div className="birthday-details">
                    <span className="next-age">
                      {genderText} {missedPercent}% מהאימונים
                    </span>
                  </div>
                </StudentCard>
              );
            })
          ) : (
            <p className="no-birthdays">אין ספורטאים</p>
          )}
        </div>
      </div>
    );
  };

  const SubPane3 = () => {
    const getUpcomingEvents = () => {
      const today = new Date();
      return events
        .filter((event) => {
          if (!event?.date?.toDate) return false;
          const eventDate = event.date.toDate();
          return eventDate >= today;
        })
        .sort((a, b) => {
          if (!a?.date?.toDate || !b?.date?.toDate) return 0;
          return a.date.toDate() - b.date.toDate();
        })
        .slice(0, 2);
    };

    const getDaysUntilEvent = (eventDate) => {
      if (!eventDate?.toDate) return 0;
      const today = new Date();
      const date = eventDate.toDate();
      const diffTime = date - today;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const formatDate = (eventDate) => {
      if (!eventDate?.toDate) return "תאריך לא ידוע";
      const date = eventDate.toDate();
      return `${date.getDate().toString().padStart(2, "0")}/${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}/${date.getFullYear()}`;
    };

    const upcomingEvents = getUpcomingEvents();

    return (
      <div className="sub-pane">
        <h6>אירועים מתקרבים</h6>
        <div className="birthday-cards">
          {upcomingEvents.length > 0 ? (
            upcomingEvents.map((event, index) => (
              <div
                key={index}
                className="birthday-card"
                onClick={() => setCurrentTab("events")}
              >
                <div className="birthday-card-content">
                  <h4 className="student-name">{event.name}</h4>
                  <div className="birthday-details">
                    <span className="next-age">
                      יתרחש בעוד {getDaysUntilEvent(event.date)} ימים בתאריך{" "}
                      {formatDate(event.date)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="no-birthdays">אין אירועים</p>
          )}
        </div>
      </div>
    );
  };

  const SubPane4 = () => {
    const { monthData, weekData, month, week } = getCurrentMonthAndWeek();
    const monthName = getMonthName(month);

    return (
      <div className="sub-pane">
        <h6>תוכנית אימונים</h6>
        <div className="birthday-cards">
          <div
            className="birthday-card"
            onClick={openTrainingPlanModal}
            style={{ cursor: "pointer" }}
          >
            <div className="birthday-card-content">
              <h4 className="student-name">חודש {monthName}</h4>
              <div className="birthday-details">
                <span className="next-age">
                  {monthData?.title ? truncateText(monthData.title, 40) : "אין תוכן לחודש זה"}
                </span>
              </div>
            </div>
          </div>
          <div
            className="birthday-card"
            onClick={openTrainingPlanModal}
            style={{ cursor: "pointer" }}
          >
            <div className="birthday-card-content">
              <h4 className="student-name">שבוע {week}</h4>
              <div className="birthday-details">
                <span className="next-age">
                  {weekData?.title ? truncateText(weekData.title, 40) : "אין תוכן לשבוע זה"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Array of sub-panes
  const subPanes = [SubPane1, SubPane2, SubPane3, SubPane4];

  return (
    <div
      className={`dynamic-updates-pane`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <button
        className="nav-button prev-button"
        onClick={goToNextPane}
        aria-label="Previous pane"
      >
        <FaChevronLeft />
      </button>
      <div
        className={`carousel-container ${isTransitioning ? "fade-out" : "fade-in"
          }`}
      >
        {subPanes[currentPane]()}
      </div>
      <button
        className="nav-button next-button"
        onClick={goToPreviousPane}
        aria-label="Next pane"
      >
        <FaChevronRight />
      </button>
      <div className="carousel-indicators">
        {Array.from({ length: totalPanes }).map((_, index) => (
          <div
            key={index}
            className={`indicator ${currentPane === index ? "active" : ""}`}
            onClick={() => {
              setIsTransitioning(true);
              setTimeout(() => {
                setCurrentPane(index);
                setIsTransitioning(false);
              }, 300);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default DynamicUpdatesPane;
